import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { OrbitalTrackRentalPopupType } from '@apeiron/orbital'
import { PlanetLite } from '@apeiron/planet'
import { StarLite } from '@apeiron/star'
import { INFO_POPUP_TYPE, InfoPopup } from '@apeiron/temp'
import { Done as DoneIcon } from '@mui/icons-material'
import {
  Box,
  List,
  ListItemButton,
  styled,
  Typography,
  useMediaQuery
} from '@mui/material'
import RentalConfirmSection from '@src/components/orbitalTrack/detachRenew/RentalConfirmSection'
import RentalTermsSection from '@src/components/orbitalTrack/detachRenew/RentalTermsSection'
import SingleRentalView from '@src/components/orbitalTrack/detachRenew/SingleRentalView'
import ApeironRedCrossIcon from '@src/components/share/apeiron/ApeironRedCrossIcon'
import { useOrbitalTrackRenewDetachContext } from '@src/contexts/orbitalTrack/OrbtialTrackRenewDetachContext'
import useOrbitalTrackHelper from '@src/hooks/apeiron/orbitalTrack/useOrbitalTrackHelper'
import theme from '@src/styles/theme'
import {
  OrbitalTrackCompleteDetachDetail,
  OrbitalTrackLite,
  OrbitalTrackSetting
} from '@src/types/orbitalTrack'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Popup = styled(InfoPopup)`
  background-color: #171719;
  height: 95vh;
  max-height: 800px;
  max-width: 1200px;
  width: 100vw;
`

const Container = styled(Box)`
  color: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`

const MainContainer = styled(Box)`
  display: flex;
  flex: 1;
  width: 100%;
  max-height: calc(100% - 87px);
`

const TabContainer = styled(Box)`
  background-color: #1b1c1d;
  display: flex;
  flex-direction: column;
  max-width: 250px;
  width: 30%;
`

const StyledList = styled(List)`
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
`

const ListItem = styled(ListItemButton)`
  background-color: #27282a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  border-color: #27282a;
  border-style: solid;
  border-width: 1px 0px 1px 1px;
  color: white;
  height: 40px;
  white-space: nowrap;
  :hover,
  :focus {
    border-color: #444548;
    background: #444548;
  }
  &.Mui-selected {
    border-color: #ffffff;
    border-radius: 3px;
    border-right: unset;
    background: #27282a;
    margin-right: -1px;
    :hover,
    :active {
      background: #444548;
    }
  }
  ${props => props.theme.breakpoints.up('res480')} {
    font-size: 16px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    padding: 10px;
  }
`

const StyledDoneIcon = styled(DoneIcon)`
  color: #8ee161;
  font-size: 20px;
  margin-left: auto;
`

const StyledCrossIcon = styled(ApeironRedCrossIcon)`
  margin-left: auto;
`

const OwnerDetachBox = styled(Box)`
  align-items: center;
  background-color: #78a5ff;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  color: black;
  display: flex;
  font-size: 12px;
  gap: 4px;
  justify-content: space-between;
  line-height: 14px;
  ${props => props.theme.breakpoints.up('res768')} {
    font-weight: 500;
    padding: 8px 16px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    font-weight: 300;
    padding: 5px 12px;
  }
`

const CompletedBox = styled(OwnerDetachBox)`
  background-color: #8bca69;
`

const IncompleteBox = styled(OwnerDetachBox)`
  background-color: #fb4c00;
  color: white;
`

const GroupCount = styled(Typography)`
  font-weight: 700;
  color: black;
`

const RenewInfoContainer = styled(Box)`
  align-items: center;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 12px;
`

const InfoRow = styled(Box)`
  color: white;
  display: flex;
  flex: 0.7;
  font-size: 12px;
  justify-content: flex-start;
  flex-wrap: wrap;
  line-height: 24px;
  width: 100%;

  ${props => props.theme.breakpoints.down('res768')} {
    align-items: center;
    margin-bottom: 8px;
    flex-direction: column-reverse;
  }
`

const NumberOfTrack = styled(Typography)`
  font-size: 12px;
  line-height: 20px;
  margin-top: 2px;
  text-align: center;
`

const ListBottomPlaceholder = styled(Box)`
  flex-grow: 1;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
`

const EndEnlargedCount = styled(Typography)`
  font-weight: 700;
  font-size: 24px;
  line-height: 24px;
  color: #8ee161;
`

const InfoLabel = styled(Typography)`
  width: 100px;
  color: #ffffff66;
  font-size: 12px;
  margin-right: 10px;

  ${props => props.theme.breakpoints.down('res768')} {
    width: auto;
    text-align: center;
  }
`

const RenewGreenCount = styled(Typography)`
  color: #8ee161;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
`

const RenewOrangedCount = styled(RenewGreenCount)`
  color: #ffffff;
  font-weight: 300;
  font-size: 16px;
  strong {
    color: #ffbf36;
  }
`

const DetailContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledSingleRentalView = styled(SingleRentalView)`
  background-color: #262829;
  overflow-y: auto;
`

const StyledRentalConfirmSection = styled(RentalConfirmSection)`
  border-top: 2px dashed rgba(128, 128, 126, 0.5);
`

const RentalItemList: FC<RentalItemListProps> = props => {
  const { selectedTrack, tracks, onClick } = props

  const { failIds, successIds } = useOrbitalTrackRenewDetachContext()

  return (
    <>
      {RA.mapIndexed((track: OrbitalTrackLite) => {
        const selected = track.planetId === selectedTrack?.planetId

        return (
          <ListItem
            disableRipple
            onClick={() => onClick(track)}
            selected={selected}
            key={track.planetId}
          >
            {track.planet?.name || ''}
            {R.includes(track.planetId, successIds) && <StyledDoneIcon />}
            {R.includes(track.planetId, failIds) && <StyledCrossIcon />}
          </ListItem>
        )
      }, tracks)}
    </>
  )
}

const Content: FC<ContentProps> = props => {
  const { popupType, tracks, onClose } = props

  const { t } = useTranslation()

  const { checkIsStarOwner, checkIsPlanetOwner } = useOrbitalTrackHelper()

  const { getApeironPrice, getCurrency } = useCurrencyContext()

  const isMobile = useMediaQuery(theme.breakpoints.down('res768'))

  const {
    detachDetails,
    stars,
    fetchRentalDetachDetail,
    resetOperation,
    setDone,
    setFailIds,
    setSuccessIds
  } = useOrbitalTrackRenewDetachContext()

  const selectedTracks = useMemo(() => {
    switch (popupType) {
      case OrbitalTrackRentalPopupType.Renew:
        return R.reject(
          (track: OrbitalTrackLite) =>
            checkIsStarOwner(track) || !track.renewable,
          tracks
        )

      default: {
        return tracks
      }
    }
  }, [popupType, tracks, checkIsStarOwner])

  const [displayOwnerInfo, setDisplayOwnerInfo] =
    useState<OrbitalTrackSetting | null>(null)

  const [displayRenterInfo, setDisplayRenterInfo] =
    useState<OrbitalTrackCompleteDetachDetail | null>(null)

  const [displayStar, setDisplayStar] = useState<StarLite | null>(null)

  const [displayPlanet, setDisplayPlanet] = useState<PlanetLite | null>(null)

  const groupByCompletion = useMemo(() => {
    const now = new Date()

    return R.groupBy((item: OrbitalTrackLite) => {
      if (checkIsPlanetOwner(item)) {
        return 'owner_detach'
      }

      return new Date(item.rentalEndTime) > now
        ? 'renter_incomplete'
        : 'renter_complete'
    }, selectedTracks)
  }, [checkIsPlanetOwner, selectedTracks])

  const ownerDetach = R.propOr(
    [],
    'owner_detach',
    groupByCompletion
  ) as OrbitalTrackLite[]

  const renterComplete = R.propOr(
    [],
    'renter_complete',
    groupByCompletion
  ) as OrbitalTrackLite[]

  const renterIncomplete = R.propOr(
    [],
    'renter_incomplete',
    groupByCompletion
  ) as OrbitalTrackLite[]

  const defaultSelectedTrack = useMemo(() => {
    if (!R.isEmpty(ownerDetach)) {
      return R.propOr(null, '0', ownerDetach) as OrbitalTrackLite
    } else if (!R.isEmpty(renterComplete)) {
      return R.propOr(null, '0', renterComplete) as OrbitalTrackLite
    } else {
      return R.propOr(null, '0', renterIncomplete) as OrbitalTrackLite
    }
  }, [ownerDetach, renterComplete, renterIncomplete])

  const [selectedTrack, setSelectedTrack] = useState<OrbitalTrackLite | null>(
    defaultSelectedTrack
  )

  const handleOnDisplaySelect = useCallback(
    (track: OrbitalTrackLite | null) => {
      if (R.isNil(track)) {
        return
      }

      const isOwner = checkIsStarOwner(track)

      const star = R.find(R.propEq(track.starId, 'id'), stars) || null

      const setting = isOwner ? track.setting : null

      const detachDetail =
        R.find<OrbitalTrackCompleteDetachDetail>(
          (R.pathEq(track.starId, ['inputParam', 'starID']),
          R.pathEq(track.planetId, ['inputParam', 'planetID'])),
          detachDetails
        ) || null

      const planet = track.planet

      setDisplayOwnerInfo(setting)
      setDisplayRenterInfo(detachDetail)
      setDisplayStar(star)
      setDisplayPlanet(planet)
    },
    [checkIsStarOwner, detachDetails, stars]
  )

  const handleListitemOnClick = useCallback(
    (track: OrbitalTrackLite) => {
      setSelectedTrack(track)
      handleOnDisplaySelect(track)
    },
    [handleOnDisplaySelect]
  )

  const handleOnSuccess = useCallback(
    (ids: string[]) => {
      if (!R.isEmpty(ids)) {
        const success: string[] = ids

        setSuccessIds(success)

        const extractFails = R.pipe<any, OrbitalTrackLite[], string[]>(
          R.reject((track: OrbitalTrackLite) =>
            R.includes(track.planetId, success)
          ),
          tracks => R.pluck('planetId', tracks)
        )

        const fail: string[] = extractFails(selectedTracks)

        setFailIds(fail)

        setDone(true)
      }
    },
    [selectedTracks, setDone, setFailIds, setSuccessIds]
  )

  useEffect(() => {
    if (!R.isEmpty(selectedTracks)) {
      fetchRentalDetachDetail(selectedTracks || [])
    }
  }, [selectedTracks])

  useEffect(() => {
    if (!R.isEmpty(detachDetails)) {
      handleOnDisplaySelect(selectedTrack)
    }
  }, [detachDetails, selectedTrack, stars])

  useEffect(() => {
    resetOperation()
  }, [])

  const totalRenewRentalFee = useMemo((): string => {
    const total = renterComplete
      .map((item: OrbitalTrackLite) => BigInt(item.setting.rentalFee))
      .reduce((acc, cur) => acc + cur, BigInt(0))

    const type = R.pathOr(
      CurrencyTokenType.Usdc,
      ['0', 'setting', 'currency', 'type'],
      renterComplete
    )

    return getApeironPrice(type, String(total))
  }, [getApeironPrice, renterComplete])

  const totalAgeGain = useMemo(
    () =>
      R.sum(
        R.map((item: OrbitalTrackLite) => item.totalAgingBuff, renterComplete)
      ),
    [renterComplete]
  )

  const currency =
    renterComplete.length > 0
      ? getCurrency(renterComplete[0].setting.currencyType)
      : null

  return (
    <Container>
      <MainContainer>
        <TabContainer>
          <RenewInfoContainer>
            {popupType === OrbitalTrackRentalPopupType.Renew ? (
              <>
                <InfoRow>
                  <InfoLabel>
                    {t('godiverse.orbital_track.rental_popup.no_of_tracks')}
                    {isMobile ? '' : ':'}
                  </InfoLabel>
                  <RenewGreenCount>{R.length(selectedTracks)}</RenewGreenCount>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>
                    {t('godiverse.orbital_track.rental_popup.rental_fee')}
                    {isMobile ? '' : ':'}
                  </InfoLabel>
                  <RenewOrangedCount>
                    <Trans>
                      {t('godiverse.orbital_track.total_fee_value', {
                        price: totalRenewRentalFee,
                        currency: currency?.name
                      })}
                    </Trans>
                  </RenewOrangedCount>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>
                    {t('godiverse.orbital_track.rental_popup.age_gain')}
                    {isMobile ? '' : ':'}
                  </InfoLabel>
                  <RenewOrangedCount>
                    <Trans>
                      {t('godiverse.orbital_track.total_year', {
                        count: totalAgeGain
                      })}
                    </Trans>
                  </RenewOrangedCount>
                </InfoRow>
              </>
            ) : (
              <>
                <EndEnlargedCount>{R.length(selectedTracks)}</EndEnlargedCount>
                <NumberOfTrack>
                  {t('godiverse.orbital_track.rental_popup.no_of_tracks')}
                </NumberOfTrack>
              </>
            )}
          </RenewInfoContainer>
          <StyledList>
            {!R.isEmpty(ownerDetach) && (
              <>
                <OwnerDetachBox>
                  {t('godiverse.orbital_track.rental_popup.self_detach')}
                  <GroupCount>{R.length(ownerDetach)}</GroupCount>
                </OwnerDetachBox>
                <RentalItemList
                  selectedTrack={selectedTrack}
                  tracks={ownerDetach}
                  onClick={handleListitemOnClick}
                />
              </>
            )}
            {!R.isEmpty(renterComplete) && (
              <>
                <CompletedBox>
                  {t('godiverse.orbital_track.rental_popup.completed')}
                  <GroupCount>{R.length(renterComplete)}</GroupCount>
                </CompletedBox>
                <RentalItemList
                  selectedTrack={selectedTrack}
                  tracks={renterComplete}
                  onClick={handleListitemOnClick}
                />
              </>
            )}
            {popupType !== OrbitalTrackRentalPopupType.Renew &&
              !R.isEmpty(renterIncomplete) && (
                <>
                  <IncompleteBox>
                    {t('godiverse.orbital_track.rental_popup.early_terminate')}
                    <GroupCount>{R.length(renterIncomplete)}</GroupCount>
                  </IncompleteBox>
                  <RentalItemList
                    selectedTrack={selectedTrack}
                    tracks={renterIncomplete}
                    onClick={handleListitemOnClick}
                  />
                </>
              )}
          </StyledList>
          <ListBottomPlaceholder />
        </TabContainer>
        <DetailContainer>
          <StyledSingleRentalView
            hideConfirmButton
            planet={displayPlanet}
            popupType={popupType}
            rentalDetail={displayRenterInfo?.detachDetail}
            setting={displayOwnerInfo}
            star={displayStar}
            onConfirm={onClose}
            onSuccess={handleOnSuccess}
          />
          <RentalTermsSection popupType={popupType} />
        </DetailContainer>
      </MainContainer>
      <StyledRentalConfirmSection
        isMultipleView
        popupType={popupType}
        tracks={selectedTracks}
        onConfirm={onClose}
        onSuccess={handleOnSuccess}
      />
    </Container>
  )
}

const MultipleRentalDialog: FC<Props> = props => {
  const { className, isOpened, onClose, ...rest } = props

  const handleOnClose = useCallback(() => {
    if (onClose) {
      onClose([])
    }
  }, [onClose])

  return (
    <Popup
      className={className}
      keepMounted={false}
      open={isOpened}
      type={INFO_POPUP_TYPE.BLACK}
      onClose={handleOnClose}
    >
      <Content {...rest} onClose={onClose} />
    </Popup>
  )
}

type RentalItemListProps = {
  selectedTrack: OrbitalTrackLite | null
  tracks: OrbitalTrackLite[]
  onClick: (item: OrbitalTrackLite) => void
}

type ContentProps = {
  popupType: OrbitalTrackRentalPopupType
  tracks: OrbitalTrackLite[]
  onClose?: (successIds: string[]) => void
}

type Props = ContentProps & {
  className?: string
  isOpened: boolean
}

export default MultipleRentalDialog
